import { useCallback } from 'react';
import type { DraftHistoryMeta } from '@bliks-io/contracts';
import { useUndoDraft, useRedoDraft } from '@/api/mutations/useProcessDraft';
import { toastApiError } from '@/api/mutations/toast-api-error';

export interface DraftUndoControls {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  isBusy: boolean;
}

/**
 * Thin client side of the persistent draft history (ADR 0007). Undo/redo are
 * server calls; the button state comes from the draft response's `history`
 * meta (so it survives reload). No in-memory stack, no per-action inverses.
 */
export function useDraftUndoControls(
  processId: string,
  history: DraftHistoryMeta | undefined
): DraftUndoControls {
  const undoMutation = useUndoDraft(processId);
  const redoMutation = useRedoDraft(processId);
  const isBusy = undoMutation.isPending || redoMutation.isPending;

  const canUndo = !!history?.canUndo && !isBusy;
  const canRedo = !!history?.canRedo && !isBusy;

  const undo = useCallback(async () => {
    if (!canUndo) return;
    try {
      await undoMutation.mutateAsync();
    } catch (error) {
      toastApiError(error, 'errors.unknown');
    }
  }, [canUndo, undoMutation]);

  const redo = useCallback(async () => {
    if (!canRedo) return;
    try {
      await redoMutation.mutateAsync();
    } catch (error) {
      toastApiError(error, 'errors.unknown');
    }
  }, [canRedo, redoMutation]);

  return { undo, redo, canUndo, canRedo, isBusy };
}
